import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './SafeIcon';
import promptService from '../services/promptService';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const { 
  FiX, FiSave, FiEdit3, FiRefreshCw, FiTarget, FiTrendingUp, FiActivity, 
  FiMaximize2, FiMinimize2, FiDatabase, FiUpload, FiDownload, FiTrash2, 
  FiCheck, FiAlertCircle, FiCode, FiEye, FiCopy, FiBookOpen, FiZap,
  FiLayers, FiSettings, FiShield, FiGlobe, FiBarChart2, FiLock
} = FiIcons;

const SUPER_ADMIN_EMAIL = 'trendykingsuccess@gmail.com';

const UniversalPromptEditor = ({ isOpen, onClose, market }) => {
  const { user } = useAuth();
  // ✅ Configuration des marchés avec sous-catégories pour indices
  const marketCategories = {
    gold: {
      key: 'gold',
      label: 'Gold',
      icon: FiTarget,
      color: 'from-yellow-500 to-orange-500',
      description: '🏆 Institutional Gold Analysis',
      subMarkets: []
    },
    indices: {
      key: 'indices',
      label: 'Indices',
      icon: FiTrendingUp,
      color: 'from-blue-500 to-cyan-500',
      description: '📈 US & European Indices',
      subMarkets: [
        { key: 'us30', label: 'US30', fullName: 'Dow Jones', icon: FiBarChart2, color: 'from-blue-600 to-blue-400' },
        { key: 'nas100', label: 'NAS100', fullName: 'NASDAQ 100', icon: FiTrendingUp, color: 'from-green-600 to-green-400' },
        { key: 'spp500', label: 'SPP500', fullName: 'S&P 500', icon: FiActivity, color: 'from-purple-600 to-purple-400' },
        { key: 'ger30', label: 'GER30', fullName: 'DAX 40', icon: FiGlobe, color: 'from-yellow-600 to-yellow-400' }
      ]
    },
    crypto: {
      key: 'crypto',
      label: 'Crypto',
      icon: FiActivity,
      color: 'from-purple-500 to-pink-500',
      description: '₿ BTC, ETH & Altcoins',
      subMarkets: [
        { key: 'btcusd', label: 'BTC', fullName: 'Bitcoin', icon: FiActivity, color: 'from-orange-600 to-orange-400' },
        { key: 'ethusd', label: 'ETH', fullName: 'Ethereum', icon: FiLayers, color: 'from-blue-600 to-purple-400' },
        { key: 'solusd', label: 'SOL', fullName: 'Solana', icon: FiZap, color: 'from-purple-600 to-green-400' },
        { key: 'xrpusd', label: 'XRP', fullName: 'Ripple', icon: FiGlobe, color: 'from-gray-600 to-gray-400' },
        { key: 'adausd', label: 'ADA', fullName: 'Cardano', icon: FiShield, color: 'from-blue-600 to-blue-400' },
        { key: 'dogeusd', label: 'DOGE', fullName: 'Dogecoin', icon: FiTrendingUp, color: 'from-yellow-600 to-yellow-400' },
        { key: 'avaxusd', label: 'AVAX', fullName: 'Avalanche', icon: FiActivity, color: 'from-red-600 to-red-400' },
        { key: 'linkusd', label: 'LINK', fullName: 'Chainlink', icon: FiSettings, color: 'from-blue-600 to-cyan-400' },
        { key: 'maticusd', label: 'MATIC', fullName: 'Polygon', icon: FiLayers, color: 'from-purple-600 to-purple-400' },
        { key: 'dotusd', label: 'DOT', fullName: 'Polkadot', icon: FiGlobe, color: 'from-pink-600 to-pink-400' }
      ]
    }
  };

  const [prompts, setPrompts] = useState({});
  const [activeCategory, setActiveCategory] = useState('gold');
  const [activeSubMarket, setActiveSubMarket] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState('');

  // Déterminer le marché actif (sous-marché ou catégorie principale)
  const getActiveMarket = () => {
    if (activeSubMarket) return activeSubMarket;
    return activeCategory;
  };

  // ✅ CHARGEMENT DES PROMPTS
  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingDetails('Connecting to database...');
      
      const defaultPrompts = promptService.getDefaultPrompts();
      const loadedPrompts = { ...defaultPrompts };
      
      // Charger tous les marchés (incluant toutes les cryptos)
      const allMarkets = [
        'gold', 'indices', 'crypto', 
        'us30', 'nas100', 'spp500', 'ger30',
        'btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 'dogeusd', 'avaxusd', 'linkusd', 'maticusd', 'dotusd'
      ];
      
      for (const marketKey of allMarkets) {
        try {
          setLoadingDetails(`Loading ${marketKey.toUpperCase()} prompt...`);
          const promptContent = await promptService.getPrompt(marketKey);
          
          if (promptContent && promptContent.trim().length > 0) {
            loadedPrompts[marketKey] = promptContent;
            console.log(`✅ Loaded ${marketKey} prompt (${promptContent.length} chars)`);
          }
        } catch (marketError) {
          console.error(`❌ Error loading ${marketKey} prompt:`, marketError);
        }
      }
      
      setPrompts(loadedPrompts);
      setLoadingDetails('Prompts loaded!');
      
    } catch (err) {
      console.error('❌ Error loading prompts:', err);
      setPrompts(promptService.getDefaultPrompts());
      setError(`Default prompts loaded (error: ${err.message})`);
    } finally {
      setLoading(false);
      setLoadingDetails('');
    }
  };

  // ✅ CHARGER AU DÉMARRAGE
  useEffect(() => {
    if (isOpen) {
      loadPrompts();
      
      // Déterminer la catégorie active basée sur le market prop
      const marketLower = market?.toLowerCase() || 'gold';
      const cryptoMarkets = ['btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 'dogeusd', 'avaxusd', 'linkusd', 'maticusd', 'dotusd'];
      
      if (['us30', 'nas100', 'spp500', 'ger30'].includes(marketLower)) {
        setActiveCategory('indices');
        setActiveSubMarket(marketLower);
      } else if (cryptoMarkets.includes(marketLower)) {
        setActiveCategory('crypto');
        setActiveSubMarket(marketLower);
      } else if (['gold', 'indices', 'crypto'].includes(marketLower)) {
        setActiveCategory(marketLower);
        if (marketLower === 'indices') setActiveSubMarket('us30');
        else if (marketLower === 'crypto') setActiveSubMarket('btcusd');
        else setActiveSubMarket(null);
      } else {
        setActiveCategory('gold');
        setActiveSubMarket(null);
      }
    }
  }, [isOpen, market]);

  // ✅ SAUVEGARDER
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const activeMarket = getActiveMarket();
      const currentPromptContent = prompts[activeMarket];
      
      if (!currentPromptContent || currentPromptContent.trim().length === 0) {
        throw new Error('Prompt content cannot be empty');
      }
      
      await promptService.savePrompt(activeMarket, currentPromptContent);
      
      promptService.clearCache();
      const verifyContent = await promptService.getPrompt(activeMarket);
      
      if (verifyContent?.trim() === currentPromptContent.trim()) {
        setSuccess(`✅ ${activeMarket.toUpperCase()} prompt saved and verified!`);
      } else {
        setSuccess(`✅ ${activeMarket.toUpperCase()} prompt saved!`);
      }
      
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      console.error('❌ Save error:', err);
      setError(`Error saving: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ✅ RESET
  const handleReset = async (marketKey) => {
    const confirmReset = window.confirm(
      `Do you really want to reset the ${marketKey.toUpperCase()} prompt to default?`
    );
    
    if (!confirmReset) return;
    
    try {
      await promptService.deletePrompt(marketKey);
      const defaultPrompts = promptService.getDefaultPrompts();
      
      setPrompts(prev => ({
        ...prev,
        [marketKey]: defaultPrompts[marketKey]
      }));
      
      setSuccess(`✅ ${marketKey.toUpperCase()} prompt reset!`);
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      setError(`Error resetting: ${err.message}`);
    }
  };

  // ✅ COPIER
  const handleCopy = async () => {
    try {
      const activeMarket = getActiveMarket();
      await navigator.clipboard.writeText(prompts[activeMarket] || '');
      setSuccess('✅ Prompt copied!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('❌ Error copying');
    }
  };

  // ✅ FORCE RELOAD
  const forceReload = async () => {
    promptService.clearCache();
    await loadPrompts();
  };

  if (!isOpen) return null;

  // ✅ SUPER ADMIN CHECK - Only trendykingsuccess@gmail.com can access prompts
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;

  const currentCategory = marketCategories[activeCategory];
  const activeMarket = getActiveMarket();
  const currentPrompt = prompts[activeMarket] || '';
  const defaultPrompts = promptService.getDefaultPrompts();
  const isCustomPrompt = currentPrompt !== defaultPrompts[activeMarket];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        className={`bg-gray-900/95 backdrop-blur-xl rounded-2xl flex flex-col border border-white/10 shadow-2xl shadow-black/50 transition-all duration-300 ${
          isFullscreen 
            ? 'w-full h-full max-w-none max-h-none rounded-none' 
            : 'w-full max-w-5xl h-[90vh]'
        }`}
      >
        {/* ✅ ACCESS DENIED FOR NON-SUPER ADMIN */}
        {!isSuperAdmin ? (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <SafeIcon icon={FiLock} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Restricted Access</h2>
                  <p className="text-xs text-gray-400">Reserved for super administrator only</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="max-w-md text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <SafeIcon icon={FiLock} className="w-10 h-10 text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  Access Denied
                </h3>
                <p className="text-gray-400 mb-2">
                  This section is exclusively reserved for the platform super administrator.
                </p>
                <p className="text-sm text-gray-500">
                  AI prompts contain Snap Chart AI's proprietary logic and can only be viewed by the platform creator.
                </p>
                <div className="mt-6 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-xs text-amber-300">
                    🔒 Current connection: <span className="font-mono">{user?.email || 'Not identified'}</span>
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
        {/* ✅ HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentCategory.color} flex items-center justify-center`}>
              <SafeIcon icon={currentCategory.icon} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Prompt Editor</h2>
              <p className="text-xs text-gray-400">{activeMarket.toUpperCase()} - Customize your AI analysis</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-all"
            >
              <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ✅ MESSAGES TOAST */}
        {(error || success) && (
          <div className="px-6 py-3">
            {error && (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 w-4 h-4 flex-shrink-0" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <SafeIcon icon={FiCheck} className="text-emerald-400 w-4 h-4 flex-shrink-0" />
                <span className="text-emerald-300 text-sm">{success}</span>
              </div>
            )}
          </div>
        )}

        {/* ✅ TABS CATÉGORIES PRINCIPALES */}
        <div className="px-6 py-4 border-b border-white/5">
          <div className="flex gap-2 mb-4">
            {Object.values(marketCategories).map((config) => {
              const isActive = activeCategory === config.key;
              
              return (
                <button
                  key={config.key}
                  onClick={() => {
                    setActiveCategory(config.key);
                    // Si indices ou crypto, activer le premier sous-marché
                    if (config.subMarkets && config.subMarkets.length > 0) {
                      setActiveSubMarket(config.subMarkets[0].key);
                    } else {
                      setActiveSubMarket(null);
                    }
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl transition-all border ${
                    isActive
                      ? 'bg-gradient-to-br ' + config.color + ' border-transparent shadow-lg'
                      : 'bg-white/5 border-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <SafeIcon icon={config.icon} className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                    <span className={`font-medium text-sm ${isActive ? 'text-white' : 'text-gray-300'}`}>{config.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ✅ SOUS-TABS POUR INDICES ET CRYPTO INDIVIDUELS */}
          {currentCategory.subMarkets && currentCategory.subMarkets.length > 0 && (
            <div className={`grid gap-2 mt-3 ${currentCategory.subMarkets.length > 6 ? 'grid-cols-5' : 'grid-cols-4'}`}>
              {currentCategory.subMarkets.map((subMarket) => {
                const isActiveSubMarket = activeSubMarket === subMarket.key;
                const hasCustomPrompt = prompts[subMarket.key] !== defaultPrompts[subMarket.key];
                
                return (
                  <button
                    key={subMarket.key}
                    onClick={() => setActiveSubMarket(subMarket.key)}
                    className={`px-2 py-2 rounded-lg transition-all border ${
                      isActiveSubMarket
                        ? 'bg-gradient-to-br ' + subMarket.color + ' border-transparent shadow-md'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={subMarket.icon} className={`w-3 h-3 ${isActiveSubMarket ? 'text-white' : 'text-gray-400'}`} />
                        <span className={`font-medium text-xs ${isActiveSubMarket ? 'text-white' : 'text-gray-300'}`}>
                          {subMarket.label}
                        </span>
                        {hasCustomPrompt && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                        )}
                      </div>
                      <span className={`text-[9px] ${isActiveSubMarket ? 'text-white/70' : 'text-gray-500'}`}>
                        {subMarket.fullName}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ✅ ACTIONS RAPIDES */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-400">
              {currentCategory.description}
              {isCustomPrompt && (
                <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                  Personnalisé
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-xs transition-all"
              >
                <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
                Copier
              </button>
              <button
                onClick={() => handleReset(activeMarket)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg text-xs transition-all"
              >
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
                Reset
              </button>
              <button
                onClick={forceReload}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-xs transition-all"
              >
                <SafeIcon icon={FiDatabase} className="w-3.5 h-3.5" />
                Reload
              </button>
            </div>
          </div>
        </div>

        {/* ✅ TEXTAREA PROMPT */}
        <div className="flex-1 px-6 py-4 overflow-hidden">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-400 text-sm">{loadingDetails || 'Loading...'}</p>
            </div>
          ) : (
            <textarea
              value={currentPrompt}
              onChange={(e) => setPrompts(prev => ({
                ...prev,
                [activeMarket]: e.target.value
              }))}
              className="w-full h-full bg-gray-800/50 border border-white/10 rounded-xl p-4 text-gray-200 text-sm font-mono resize-none focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all"
              placeholder={`Enter your custom prompt for ${activeMarket.toUpperCase()}...`}
              spellCheck={false}
            />
          )}
        </div>

        {/* ✅ FOOTER */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/5">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <SafeIcon icon={FiDatabase} className="w-3.5 h-3.5" />
              <span>Market: {activeMarket.toUpperCase()}</span>
            </div>
            <span>•</span>
            <span>{currentPrompt.length} characters</span>
            {isCustomPrompt && (
              <>
                <span>•</span>
                <span className="text-emerald-400">Custom</span>
              </>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                saving
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white shadow-lg shadow-blue-500/25'
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
        </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default UniversalPromptEditor;
